import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { can, ROLE_LABELS } from "@/lib/permissions";
import {
  LayoutDashboard,
  Wallet,
  Building2,
  Users,
  ReceiptText,
  Gauge,
  ScrollText,
  Bell,
  FileText,
  CreditCard,
  Plug,
  Gift,
  UserCog,
  Inbox,
  ShieldCheck,
  History,
  LifeBuoy,
  FlaskConical,
  BookOpen,
  ClipboardList,
  Target,
  LogOut,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, tid: "nav-overview" },
  { to: "/app/budgets", label: "Budget Center", icon: Wallet, tid: "nav-budgets" },
  { to: "/app/vendors", label: "Vendors & Contracts", icon: Building2, tid: "nav-vendors" },
  { to: "/app/profile", label: "Firm Profile", icon: ClipboardList, tid: "nav-profile" },
  { to: "/app/leads", label: "Lead-to-Case", icon: Users, tid: "nav-leads" },
  { to: "/app/invoices", label: "Invoice Reconciliation", icon: ReceiptText, tid: "nav-invoices" },
  { to: "/app/scorecards", label: "Vendor Scorecards", icon: Gauge, tid: "nav-scorecards" },
  { to: "/app/compliance", label: "Compliance Archive", icon: ScrollText, tid: "nav-compliance" },
  { to: "/app/alerts", label: "Alerts & Digest", icon: Bell, tid: "nav-alerts" },
  { to: "/app/report", label: "Executive Report", icon: FileText, tid: "nav-report" },
  { to: "/app/integrations", label: "Integrations", icon: Plug, tid: "nav-integrations", perm: "integration:manage" },
  { to: "/app/billing", label: "Billing & Plans", icon: CreditCard, tid: "nav-billing", perm: "billing:manage" },
  { to: "/app/team", label: "Team & Roles", icon: UserCog, tid: "nav-team", perm: "user:manage" },
  { to: "/app/inquiries", label: "Leads & Inquiries", icon: Inbox, tid: "nav-inquiries", adminOnly: true },
  { to: "/app/experiments", label: "A/B Experiments", icon: FlaskConical, tid: "nav-experiments", adminOnly: true },
  { to: "/app/playbook", label: "Sales Playbook", icon: BookOpen, tid: "nav-playbook", adminOnly: true },
  { to: "/app/baseline", label: "Baseline Campaign", icon: Target, tid: "nav-baseline", adminOnly: true },
  { to: "/app/security", label: "Security", icon: ShieldCheck, tid: "nav-security", demoHide: true },
  { to: "/app/governance", label: "Audit & Data", icon: History, tid: "nav-governance", perm: "user:manage" },
  { to: "/app/resources", label: "Resources & Help", icon: LifeBuoy, tid: "nav-resources" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showNudge, setShowNudge] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  useEffect(() => {
    if (!user?.is_demo || nudgeDismissed) return;
    const t = setTimeout(() => setShowNudge(true), 30000);
    return () => clearTimeout(t);
  }, [user, nudgeDismissed]);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const status = user?.subscription_status;
  const showBanner = status && status !== "active";

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#0B1F33] font-body flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-zinc-200 bg-white flex flex-col fixed inset-y-0">
        <div className="h-16 flex items-center gap-2.5 px-6 border-b border-zinc-200">
          <img src="/retainproof-icon-light.png" alt="RetainProof" className="h-8 w-auto" />
          <span className="font-body font-bold text-2xl tracking-tight" data-testid="sidebar-brand">
            <span className="text-[#0B1F33]">Retain</span><span className="text-[#174A7E]">Proof</span><sup className="text-[0.5em] font-normal align-super text-[#5E6B78]">™</sup>
          </span>
        </div>
        {user?.is_demo && (
          <div className="mx-3 mt-3 border border-[#D5A44A] bg-[#FBF7E9] p-3" data-testid="demo-banner">
            <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#8A6A1F]">Read-only demo</p>
            <p className="text-xs text-[#6B5410] mt-1">You're exploring sample data — changes are disabled.</p>
            <a href="/consultation" className="mt-2 inline-block font-mono-data text-[10px] uppercase tracking-widest text-[#0B1F33] underline">Request a consultation →</a>
          </div>
        )}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {nav.filter((n) => (!n.perm || can(user?.role, n.perm)) && (!n.adminOnly || user?.is_platform_admin) && !(user?.is_demo && n.demoHide)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-[#0B1F33] text-white font-medium"
                    : "text-zinc-600 hover:bg-zinc-100"
                }`
              }
            >
              <n.icon className="h-4 w-4" strokeWidth={1.8} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-200 p-4">
          <div className="mb-3">
            <p className="text-sm font-medium truncate" data-testid="sidebar-firm-name">{user?.firm_name}</p>
            <p className="font-mono-data text-xs text-zinc-500 truncate">{user?.email}</p>
            <span className="inline-block mt-1.5 font-mono-data text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-600 px-2 py-0.5" data-testid="sidebar-role">
              {ROLE_LABELS[user?.role] || user?.role}
            </span>
          </div>
          <Button
            onClick={doLogout}
            variant="outline"
            data-testid="logout-button"
            className="w-full rounded-none justify-start gap-2 border-zinc-300 text-zinc-700 hover:bg-zinc-100"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
          <p className="mt-4 font-mono-data text-[10px] text-zinc-400 tracking-wide" data-testid="sidebar-branding">
            RetainProof™ by CMD+DesignLab
          </p>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 min-h-screen">
        {showBanner && (
          <div className="bg-[#0B1F33] text-white" data-testid="trial-banner">
            <div className="max-w-[1400px] mx-auto px-10 py-2.5 flex items-center justify-between gap-4">
              <span className="text-sm flex items-center gap-2">
                <Gift className="h-4 w-4 text-[#D5A44A]" />
                {(
                  <>Your firm isn't on an active plan yet · request guided enrollment to get set up.</>
                )}
              </span>
              <NavLink to="/app/billing" data-testid="trial-banner-cta" className="shrink-0 font-mono-data text-xs uppercase tracking-widest bg-[#D5A44A] text-black px-3 py-1.5 hover:bg-[#B8923D] transition-colors">
                View plans
              </NavLink>
            </div>
          </div>
        )}
        <div className="max-w-[1400px] mx-auto px-10 py-10">
          <Outlet />
        </div>
      </main>

      {user?.is_demo && showNudge && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[150] flex items-center gap-4 bg-[#0B1F33] text-white border border-[#D5A44A]/50 shadow-2xl pl-5 pr-3 py-3 animate-in fade-in slide-in-from-bottom-4"
          data-testid="demo-nudge"
        >
          <span className="text-sm">Like what you see? Request a consultation and track your own firm.</span>
          <a
            href="/consultation"
            data-testid="demo-nudge-cta"
            className="shrink-0 font-mono-data text-xs uppercase tracking-widest bg-[#D5A44A] text-black px-4 py-2 hover:bg-[#B8923D] transition-colors"
          >
            Request consultation
          </a>
          <button
            onClick={() => { setShowNudge(false); setNudgeDismissed(true); }}
            data-testid="demo-nudge-dismiss"
            className="text-zinc-400 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
